'use client';

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { faList } from '@fortawesome/pro-solid-svg-icons';
import { useGetAllStrategiesFilterOptionsQuery } from 'src/api/redux/strategy/allStrategiesApi';
import { useDebounce } from 'use-debounce';
import { LOCATION } from 'src/hooks/filters/constants';
import useStrategies from 'src/hooks/strategy/useStrategies';
import {
  useClickOutside,
  useDropdownPosition,
  useResizeClose,
} from 'src/hooks/common';
import {
  FilterDropdownButton,
  FilterDropdownPortal,
  CheckboxListItem,
  DropdownEmptyState,
} from '../shared';

interface ProviderList {
  id: string;
  name: string;
}

const StrategyProviderListDropdown: React.FC = (): React.ReactElement => {
  const {
    boundedTerritory,
    selectedTerritory,
    selectedChainIds,
    selectedProviderLists,
    setSelectedProviderLists,
  } = useStrategies(LOCATION.STRATEGY_CENTER);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 500);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [allProviderLists, setAllProviderLists] = useState<ProviderList[]>([]);
  const isLoadingNextPage = useRef<boolean>(false);

  const { data: providerLists, isFetching } =
    useGetAllStrategiesFilterOptionsQuery(
      {
        type: 'list-membership',
        chain_id: selectedChainIds,
        territory_id: selectedTerritory?.id,
        bounding_territory: boundedTerritory?.id,
        q: debouncedSearch,
        page,
        pageSize,
      },
      {
        skip: selectedChainIds.length === 0,
      }
    );

  const hasMore = providerLists?.results?.length === pageSize;

  const { dropdownStyle, calculatePosition } = useDropdownPosition(
    buttonRef,
    300
  );

  useClickOutside([buttonRef, dropdownRef], () => setIsOpen(false), isOpen);
  useResizeClose(() => setIsOpen(false), isOpen);

  useEffect(() => {
    setPage(1);
    setAllProviderLists([]);
    isLoadingNextPage.current = false;
  }, [debouncedSearch, selectedChainIds, boundedTerritory?.id, selectedTerritory?.id]);

  useEffect(() => {
    if (providerLists?.results) {
      setAllProviderLists((prev) => {
        const validLists = providerLists.results.filter(
          (l: ProviderList) => l.name
        );
        if (page === 1) {
          isLoadingNextPage.current = false;
          return validLists;
        }
        const existingIds = new Set(prev.map((l: ProviderList) => l.id));
        const newLists = validLists.filter(
          (l: ProviderList) => !existingIds.has(l.id)
        );
        isLoadingNextPage.current = false;
        return [...prev, ...newLists];
      });
    }
  }, [providerLists, page]);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || isFetching || !hasMore || isLoadingNextPage.current)
      return;

    const { scrollTop, scrollHeight, clientHeight } = container;

    if (scrollTop + clientHeight >= scrollHeight - 100) {
      isLoadingNextPage.current = true;
      setPage((prev) => prev + 1);
    }
  }, [isFetching, hasMore]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !isOpen) return;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll, isOpen]);

  const isDisabled = selectedChainIds.length === 0;

  const handleClick = () => {
    if (isDisabled) return;

    if (!isOpen) {
      calculatePosition();
    }

    setIsOpen(!isOpen);
  };

  const handleProviderListToggle = (providerListId: string) => {
    const updatedLists = selectedProviderLists.includes(providerListId)
      ? selectedProviderLists.filter((l) => l !== providerListId)
      : [...selectedProviderLists, providerListId];
    setSelectedProviderLists(updatedLists);
  };

  const handleClearProviderLists = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedProviderLists([]);
  };

  const getDisplayText = () => {
    if (selectedProviderLists.length === 0) return 'Provider Lists';
    if (selectedProviderLists.length === 1) {
      return (
        allProviderLists.find((l) => l.id === selectedProviderLists[0])?.name ||
        selectedProviderLists[0]
      );
    }
    return `${selectedProviderLists.length} Lists`;
  };

  return (
    <>
      <FilterDropdownButton
        icon={faList}
        label="Provider List"
        isOpen={isOpen}
        isDisabled={isDisabled}
        displayText={getDisplayText()}
        hasSelection={selectedProviderLists.length > 0}
        onClear={handleClearProviderLists}
        onClick={handleClick}
        buttonRef={buttonRef}
        testId="strategy-provider-list-dropdown"
      />

      <FilterDropdownPortal
        isOpen={isOpen}
        dropdownRef={dropdownRef}
        scrollContainerRef={scrollContainerRef}
        dropdownStyle={dropdownStyle}
        searchPlaceholder="Search provider lists..."
        searchValue={search}
        onSearchChange={setSearch}
        selectedCount={selectedProviderLists.length}
        onClearAll={() => setSelectedProviderLists([])}
      >
        {allProviderLists.map((providerList) => (
          <CheckboxListItem
            key={providerList.id}
            id={providerList.id}
            label={providerList.name}
            isSelected={selectedProviderLists.includes(providerList.id)}
            onToggle={handleProviderListToggle}
          />
        ))}

        <DropdownEmptyState
          hasResults={allProviderLists.length > 0}
          isLoading={isFetching}
          searchTerm={search}
          emptyMessage="matching lists"
        />
      </FilterDropdownPortal>
    </>
  );
};

export default StrategyProviderListDropdown;
